import React from 'react';
import { Link } from 'react-router-dom';
import { Checkbox } from './ui/checkbox';

/**
 * TCPA-Compliant SMS Consent Block — SimpleTexting Approved Language
 *
 * Renders SimpleTexting's REQUIRED disclosure word-for-word, with:
 *  - Single non-mandatory checkbox (unchecked by default)
 *  - "Promotional" + "autodialer" + "not a condition of any purchase" language
 *  - Explicit message frequency ("4 Msgs/Month")
 *  - "Msg & Data rates may apply"
 *  - "Text STOP to opt-out" + "Text HELP for assistance"
 *  - Terms of Service + Privacy Policy links
 *  - Third-party / affiliate exclusion clause
 *
 * IMPORTANT: This block is ALWAYS visible on every form that collects a phone
 * number — never gated by a phone-field state. SimpleTexting requires the
 * disclosure to be present at the point the phone is requested so the user
 * can read it before typing their number.
 *
 * @param {Object}   props
 * @param {boolean}  props.checked          - Controlled checkbox state
 * @param {Function} props.onCheckedChange  - Callback when checkbox toggles
 * @param {string}   props.checkboxId       - Unique DOM id for checkbox
 * @param {string}   props.className        - Additional Tailwind classes
 */
export function SMSConsentBlock({
  checked = false,
  onCheckedChange,
  checkboxId = 'sms-consent',
  className = '',
}) {
  return (
    <div
      className={`p-4 bg-slate-50 rounded-lg border border-slate-200 ${className}`}
      data-testid="sms-consent-block"
    >
      {/* ── Primary opt-in: checkbox + SimpleTexting-required disclosure ── */}
      <div className="flex items-start gap-3">
        <Checkbox
          id={checkboxId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          className="mt-1 shrink-0"
          data-testid="sms-consent-checkbox"
        />
        <label
          htmlFor={checkboxId}
          className="text-[13px] text-slate-700 leading-relaxed cursor-pointer select-none"
          data-testid="sms-consent-label"
        >
          I agree to receive <strong>promotional</strong> messages from TRITAL Care
          {' '}<strong>sent via an autodialer</strong>, and this agreement{' '}
          <strong>isn&apos;t a condition of any purchase</strong>. I also agree to the{' '}
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-trital-navy underline underline-offset-2 font-semibold hover:text-trital-red"
            data-testid="sms-consent-terms-link"
          >
            Terms of Service
          </Link>
          {' '}and{' '}
          <Link
            to="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-trital-navy underline underline-offset-2 font-semibold hover:text-trital-red"
            data-testid="sms-consent-privacy-link"
          >
            Privacy Policy
          </Link>
          . <strong>4 Msgs/Month.</strong> Msg & Data rates may apply.{' '}
          <strong>Text STOP to opt-out.</strong> Text HELP for assistance.
        </label>
      </div>

      {/* ── Third-party / affiliate exclusion clause (SimpleTexting required) ── */}
      <p
        className="mt-3 pl-7 text-[11.5px] text-slate-500 leading-relaxed"
        data-testid="sms-third-party-disclosure"
      >
        By submitting this form, I agree that my mobile information will not be
        shared with third parties/affiliates for marketing/promotional purposes.
        All the above categories exclude text messaging originator opt-in data
        and consent; this information will not be shared with any third parties,
        except for the necessary opt-in data required to facilitate the SMS service.
      </p>
    </div>
  );
}

export default SMSConsentBlock;
